import { startCase } from 'lodash';
import * as Yup from 'yup';

import { StateObject } from '../constants/states';

// Helper
const stringIsNumber = (value: string | number) =>
    isNaN(Number(value)) !== false;

export function enumToKeyList<R extends object = object>(
    enumme: R
): Array<keyof R> {
    return Object.values(enumme).filter(stringIsNumber);
}

export function enumToList<R = string>(enumObject: Record<string, R>) {
    return Object.values<R>(enumObject);
}

export const stringListToFormOptions = (list: string[]): EnumFormOption[] =>
    list.map(s => ({ id: s, label: startCase(s), value: s }));

export const enumToFormOptions = (
    enumObject: Record<string, string>
): EnumFormOption[] => stringListToFormOptions(enumToList(enumObject));

export const stateListToFormOptions = (states: StateObject[]) =>
    states.map(({ abbreviation, name }) => ({
        id: abbreviation,
        label: name,
        value: abbreviation,
    }));

export const transformEmptyToNull = (value?: string) => {
    return !value ? null : value;
};

export interface EnumFormOption {
    id: string;
    label: string;
    value: string;
}

export function atLeastOneRequired<T extends object>(
    property1: keyof T,
    property2: keyof T
): Yup.TestFunction<T> {
    return function (value: T) {
        if (value[property1] === null && value[property2] === null) {
            return this.createError({
                path: property1.toString(),
                message: `At least one of ${property1.toString()} or ${property2.toString()} is required`,
            });
        }
        return true;
    };
}

export function atOneOrTheOther<T extends object>(
    property1: keyof T,
    property2: keyof T
): Yup.TestFunction<T> {
    return function (value: T) {
        const hasProperty1 = !!value[property1];
        const hasProperty2 = !!value[property2];

        if (
            (hasProperty1 && hasProperty2) ||
            (!hasProperty1 && !hasProperty2)
        ) {
            return this.createError({
                path: property1.toString(),
                message: `Either ${property1.toString()} or ${property2.toString()} must be defined, but not both.`,
            });
        }
        return true;
    };
}
const ML_PER_OZ = 29.5735;

export function volumeConsistency<T extends Record<string, number | null>>(
    property1: keyof T = 'volumeOz',
    property2: keyof T = 'volumeMl'
): Yup.TestFunction<T> {
    return function (value) {
        const { [property2]: volumeMl, [property1]: volumeOz } = value;

        if (!!volumeMl && !!volumeOz) {
            const expectedMl = +(volumeOz * ML_PER_OZ).toFixed(0);

            if (Math.abs(volumeMl - expectedMl) > 0.01) {
                return this.createError({
                    path: property1.toString(),
                    message: `${property1.toString()} and ${property2.toString()} are inconsistent`,
                });
            }
        }
        return true;
    };
}

export function volumeConversions<T extends Record<string, number>>(
    property1: keyof T = 'volumeOz',
    property2: keyof T = 'volumeMl'
) {
    return (value: T, originalValue: T) => {
        // Automatically set volumeMl or volumeOz if only one is provided
        // Generated by Copilot
        let { [property2]: volumeMl, [property1]: volumeOz } = value;
        if (!!volumeMl && (!volumeOz || isNaN(volumeOz))) {
            volumeOz = +(volumeMl / ML_PER_OZ).toFixed(2) as T[keyof T];
        } else if (!!volumeOz && (!volumeMl || isNaN(volumeMl))) {
            volumeMl = +(volumeOz * ML_PER_OZ).toFixed(0) as T[keyof T];
        }
        return { ...value, [property2]: volumeMl, [property1]: volumeOz };
    };
}
